import React, { Fragment, useCallback, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import { CodeBlock } from '@atlaskit/code';
import { AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import Heading from '@atlaskit/heading';
import BugIcon from '@atlaskit/icon/core/bug';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';

const messages = defineMessages({
	ai_content_disclaimer: {
		id: 'search_ai.ai_content_disclaimer',
		defaultMessage: 'Uses AI. Verify results.',
		description: 'Text for the AI content disclaimer',
	},
	ai_debugger_icon_label: {
		id: 'search_ai.ai_debugger_icon_label',
		defaultMessage: 'Debugger',
		description: 'Label for debugger icon',
	},
	ai_debugger_modal_title: {
		id: 'search_ai.ai_debugger_modal_title',
		defaultMessage: 'SAIN Debugging Panel',
		description: 'Title for debugging modal',
	},
	ai_debugger_modal_query_subheading: {
		id: 'search_ai.ai_debugger_modal_query_subheading',
		defaultMessage: 'Query',
		description: 'Subheading for query',
	},
	ai_debugger_modal_request_id_subheading: {
		id: 'search_ai.ai_debugger_modal_request_id_subheading',
		defaultMessage: 'Request ID',
		description: 'Subheading for request ID',
	},
	ai_debugger_modal_model_subheading: {
		id: 'search_ai.ai_debugger_modal_model_subheading',
		defaultMessage: 'Answer Generation Model',
		description: 'Subheading for model',
	},
	ai_debugger_modal_sources_subheading: {
		id: 'search_ai.ai_debugger_modal_sources_subheading',
		defaultMessage: 'Sources',
		description: 'Subheading for sources',
	},
	ai_debugger_modal_cache_subheading: {
		id: 'search_ai.ai_debugger_modal_cache_subheading',
		defaultMessage: 'Cache',
		description: 'Subheading for cache',
	},
	ai_debugger_modal_plugin_invocations_subheading: {
		id: 'search_ai.ai_debugger_modal_plugin_invocations_subheading',
		defaultMessage: 'Plugin Invocations',
		description: 'Subheading for plugin invocations',
	},
	ai_debugger_modal_copy_button_text: {
		id: 'search_ai.ai_debugger_modal_copy_button_text',
		defaultMessage: 'Copy final response JSON',
		description: 'Text for final response copy button',
	},
	ai_debugger_modal_null_text: {
		id: 'search_ai.ai_debugger_modal_null_text',
		defaultMessage: 'null',
		description: 'Text for null fields',
	},
	ai_debugger_modal_copy_button_flag_text: {
		id: 'search_ai.ai_debugger_modal_copy_button_flag_text',
		defaultMessage: 'Final response JSON copied',
		description: 'Text for null fields',
	},
});

export const OnlineDebuggerButton = () => {
	const { formatMessage } = useIntl();
	const { commonAttributes } = useAIAnswerContext();
	const [isSAINDebuggerModalOpen, setIsSAINDebuggerModalOpen] = useState(false);
	const [displayCopyFlag, setDisplayCopyFlag] = useState(false);

	const openDebuggerModal = useCallback(() => setIsSAINDebuggerModalOpen(true), []);
	const closeDebuggerModal = useCallback(() => setIsSAINDebuggerModalOpen(false), []);

	const { extraAttributes } = commonAttributes;

	const formatJson = (data: unknown): string => {
		return JSON.stringify(data, null, 2);
	};

	const copyFinalResponseJson = async (): Promise<void> => {
		try {
			if (!extraAttributes?.rawJson) {
				return;
			}

			const jsonString = formatJson(extraAttributes.rawJson);
			if (!jsonString) {
				return;
			}

			await navigator.clipboard.writeText(jsonString);
			setDisplayCopyFlag(true);
			return;
		} catch (_e) {
			return;
		}
	};

	return (
		<Fragment>
			<IconButton
				icon={BugIcon}
				label={formatMessage(messages.ai_debugger_icon_label)}
				onClick={openDebuggerModal}
				appearance="subtle"
				isTooltipDisabled={false}
				spacing="compact"
				testId="sain-online-debugger-button"
				color={token('color.icon.subtlest')}
			/>

			<ModalTransition>
				{isSAINDebuggerModalOpen && (
					<Modal onClose={closeDebuggerModal} testId="sain-online-debugger-modal">
						<ModalHeader hasCloseButton>
							<ModalTitle>{formatMessage(messages.ai_debugger_modal_title)}</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<Stack space="space.100">
								<Heading size="xsmall">
									{formatMessage(messages.ai_debugger_modal_query_subheading)}
								</Heading>
								<CodeBlock
									language="json"
									text={formatJson(extraAttributes?.userQuery ?? 'null')}
									shouldWrapLongLines
								/>
								<Heading size="xsmall">
									{formatMessage(messages.ai_debugger_modal_request_id_subheading)}
								</Heading>
								<CodeBlock
									language="json"
									text={formatJson(extraAttributes?.requestId ?? 'null')}
									shouldWrapLongLines
								/>
								<Heading size="xsmall">
									{formatMessage(messages.ai_debugger_modal_model_subheading)}
								</Heading>
								<CodeBlock
									language="json"
									text={formatJson(extraAttributes?.answerGenerationModel ?? null)}
									shouldWrapLongLines
								/>
								<Heading size="xsmall">
									{formatMessage(messages.ai_debugger_modal_sources_subheading)}
								</Heading>
								<CodeBlock
									language="json"
									text={formatJson(extraAttributes?.sources ?? null)}
									shouldWrapLongLines
								/>
								<Heading size="xsmall">
									{formatMessage(messages.ai_debugger_modal_cache_subheading)}
								</Heading>
								<CodeBlock
									language="json"
									text={formatJson(extraAttributes?.cacheInfo ?? null)}
									shouldWrapLongLines
								/>
								<Heading size="xsmall">
									{formatMessage(messages.ai_debugger_modal_plugin_invocations_subheading)}
								</Heading>
								<CodeBlock
									language="json"
									text={formatJson(extraAttributes?.pluginInvocations ?? null)}
									shouldWrapLongLines
								/>
							</Stack>
						</ModalBody>
						<ModalFooter>
							<Button appearance="primary" onClick={copyFinalResponseJson}>
								{formatMessage(messages.ai_debugger_modal_copy_button_text)}
							</Button>
						</ModalFooter>
					</Modal>
				)}
			</ModalTransition>

			<FlagGroup onDismissed={() => setDisplayCopyFlag(false)}>
				{displayCopyFlag && (
					<AutoDismissFlag
						id="copy-flag"
						icon={<SuccessIcon label="Success" color={token('color.icon.success')} />}
						title={formatMessage(messages.ai_debugger_modal_copy_button_flag_text)}
					/>
				)}
			</FlagGroup>
		</Fragment>
	);
};
